import React, { useEffect, useMemo, useState } from 'react';
import { getDocs } from 'firebase/firestore';
import { Screen, UserProfile } from '../App';
import { usersColl } from '../db';
import { canUseCycleFeature, inferGender } from '../utils/gender';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }

const SECTIONS: {
  title: string;
  items: { id: Screen; icon: string; label: string; desc: string }[];
}[] = [
  {
    title: 'Planlarımız',
    items: [
      { id: 'countdown', icon: '⌁', label: 'Geri sayım', desc: 'Buluşmaya, özel güne, beklediğimiz ana ne kaldı?' },
      { id: 'date-ideas', icon: '✦', label: 'Randevu fikirleri', desc: 'Bugün ne yapsak diye kalırsak.' },
      { id: 'tasks', icon: '✓', label: 'Haftalık hedefler', desc: 'Birlikte yapmak istediğimiz küçük şeyler.' },
    ],
  },
  {
    title: 'Küçük sürprizler',
    items: [
      { id: 'wishlist', icon: '◈', label: 'Hediye ipuçları', desc: 'Aklımızda kalsın dediğimiz şeyler.' },
      { id: 'streak', icon: '↟', label: 'Ritmimiz', desc: 'Son günlerde birbirimize ne kadar uğradık?' },
      { id: 'cycle', icon: '◌', label: 'Döngü takibi', desc: 'Regl takvimi, fazlar ve destek.' },
    ],
  },
  {
    title: 'Uygulama',
    items: [
      { id: 'settings', icon: '⌘', label: 'Ayarlar', desc: 'Profil, bildirimler ve bize özel detaylar.' },
    ],
  },
];

export default function MoreScreen({ user, navigate }: Props) {
  const [members, setMembers] = useState<UserProfile[]>([user]);

  useEffect(() => {
    getDocs(usersColl()).then(snap => {
      const sameCouple = snap.docs
        .map(d => ({ uid: d.id, ...d.data() } as UserProfile))
        .filter(member => member.coupleId === user.coupleId);
      setMembers(sameCouple.length ? sameCouple : [user]);
    }).catch(() => setMembers([user]));
  }, [user]);

  const sections = useMemo(() => {
    const cycleEnabled = canUseCycleFeature(members.length ? members : [user]);
    return SECTIONS.map(section => ({
      ...section,
      items: section.items.filter(item => item.id !== 'cycle' || cycleEnabled),
    })).filter(section => section.items.length);
  }, [members, user]);

  const genderMissing = !inferGender(user);

  return (
    <main className="hub-screen">
      <header className="hub-hero">
        <p>Biz</p>
        <h1>Bizim köşe.</h1>
        <span>Planlar, fikirler ve ayarlar burada dursun.</span>
      </header>

      {genderMissing && (
        <section className="hub-section">
          <div className="cycle-gender-hint">
            <strong>Profilini tamamla</strong>
            <span>Döngü alanı cinsiyete göre açılır. Ayarlardan Kadın/Erkek seç.</span>
            <button onClick={() => navigate('settings')}>Ayarlar</button>
          </div>
        </section>
      )}

      {sections.map(section => (
        <section key={section.title} className="hub-section">
          <h2>{section.title}</h2>
          <div className="hub-list">
            {section.items.map(item => (
              <button key={item.id} className="hub-row" onClick={() => navigate(item.id)}>
                <span>{item.icon}</span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.desc}</small>
                </div>
                <b>›</b>
              </button>
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}
